import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type CheckoutInput, posApi } from '../pos';

// Mirrors the wallet query key shape from use-wallet.ts (kept inline rather than imported —
// that file's `keys` object isn't exported — so the checkout mutation can invalidate the
// same cached wallet entry after a POS charge).
const walletKey = (userId: string) => ['cafeteria', 'wallet', userId] as const;

// Named "ResolvePerson"/"Checkout" (not "Cafeteria...") to stay unique across the
// `services/api/index.ts` barrel — see use-cafeteria.ts's note about the prior name collision.
export function useResolvePersonMutation() {
  return useMutation({
    mutationFn: (personId: string) => posApi.resolve(personId),
  });
}

// Type-ahead search for the "forgot card" flow. The caller passes an already-debounced,
// trimmed term; the query only fires at >= 2 chars (shorter terms stay disabled, matching
// the backend which returns [] below that threshold).
export function usePosSearchQuery(term: string) {
  const trimmed = term.trim();
  return useQuery({
    queryKey: ['cafeteria', 'pos', 'search', trimmed] as const,
    queryFn: () => posApi.search(trimmed),
    enabled: trimmed.length >= 2,
  });
}

// Type-ahead search for the Thai-QR till's optional attribution step (parent,
// student or staff — the API owns the default roles). Mirrors usePosSearchQuery's
// shape (caller debounces, >= 2 chars) but hits the separate attribution-search endpoint and is keyed/named distinctly so
// it doesn't collide with the "forgot card" search above in the query cache.
export function useAttributionSearchQuery(term: string, roles?: string) {
  const trimmed = term.trim();
  return useQuery({
    queryKey: ['cafeteria', 'pos', 'attribution-search', trimmed, roles] as const,
    queryFn: () => posApi.attributionSearch(trimmed, roles),
    enabled: trimmed.length >= 2,
  });
}

export function useCheckoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CheckoutInput) => posApi.checkout(input),
    onSuccess: (_data, variables) => {
      // Only the wallet path touches a balance — a thai_qr sale (even with an
      // attributed userId) never mutates the wallet, so there's nothing to invalidate.
      const paymentMethod = variables.paymentMethod ?? 'wallet';
      if (paymentMethod === 'wallet' && variables.userId) {
        qc.invalidateQueries({ queryKey: walletKey(variables.userId) });
      }
    },
  });
}
